/**
 * Model Health Panel — deployed models with health status, accuracy trends, drift events
 * Phase 4.8-B — WQ-200
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  getDashboard,
  listDriftEvents,
  resolveDriftEvent,
  type DashboardItem,
  type DriftEvent,
} from '../../services/modelHealthService';

const HEALTH_COLORS: Record<string, string> = {
  healthy: '#22c55e',
  warning: '#f59e0b',
  degraded: '#f97316',
  critical: '#ef4444',
  unknown: '#6b7280',
};

const SEVERITY_COLORS: Record<string, string> = {
  low: '#3b82f6',
  medium: '#f59e0b',
  high: '#f97316',
  critical: '#ef4444',
};

const ModelHealthPanel: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const [models, setModels] = useState<DashboardItem[]>([]);
  const [driftEvents, setDriftEvents] = useState<DriftEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [dashData, eventsData] = await Promise.all([
        getDashboard(authFetch),
        listDriftEvents(authFetch, { is_resolved: false, limit: 20 }),
      ]);
      setModels(Array.isArray(dashData) ? dashData : []);
      setDriftEvents(Array.isArray(eventsData) ? eventsData : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load model health');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!authLoading) fetchData();
  }, [authLoading]);

  const handleResolve = async (eventId: string) => {
    setResolvingId(eventId);
    try {
      await resolveDriftEvent(authFetch, eventId);
      setDriftEvents(prev => prev.filter(e => e.id !== eventId));
    } catch {
      // keep event in list on failure
    }
    setResolvingId(null);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
        <div style={{ width: 32, height: 32, border: '3px solid rgba(255,255,255,0.1)', borderTop: '3px solid #00d4ff', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass" style={{ padding: 20, textAlign: 'center' }}>
        <p style={{ color: '#ef4444', marginBottom: 12 }}>{error}</p>
        <button onClick={fetchData} className="glass" style={{ padding: '6px 16px', cursor: 'pointer', color: '#00d4ff', background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.3)', borderRadius: 6 }}>
          Retry
        </button>
      </div>
    );
  }

  const healthCounts = models.reduce<Record<string, number>>((acc, m) => {
    acc[m.overall_health] = (acc[m.overall_health] || 0) + 1;
    return acc;
  }, {});

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Summary KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
        <div className="glass" style={{ padding: 16, textAlign: 'center' }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#00d4ff' }}>{models.length}</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>Models Tracked</div>
        </div>
        {Object.entries(healthCounts).map(([health, count]) => (
          <div key={health} className="glass" style={{ padding: 16, textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: HEALTH_COLORS[health] || '#6b7280' }}>{count}</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 4, textTransform: 'capitalize' }}>{health}</div>
          </div>
        ))}
        <div className="glass" style={{ padding: 16, textAlign: 'center' }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: driftEvents.length > 0 ? '#f59e0b' : '#22c55e' }}>{driftEvents.length}</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>Unresolved Drift</div>
        </div>
      </div>

      {/* Models Table */}
      <div className="glass" style={{ padding: 20 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: 'rgba(255,255,255,0.8)' }}>
          Deployed Models
        </h3>
        {models.length === 0 ? (
          <p style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: 20 }}>No models deployed yet</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Model</th>
                  <th style={{ textAlign: 'center', padding: '8px 12px', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Health</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Predictions (24h)</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Accuracy</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Delta</th>
                  <th style={{ textAlign: 'center', padding: '8px 12px', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Drift</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', color: 'rgba(255,255,255,0.5)', fontWeight: 500 }}>Quality</th>
                </tr>
              </thead>
              <tbody>
                {models.map(m => (
                  <tr key={m.model_id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 12 }}>{m.model_id.slice(0, 8)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                        background: `${HEALTH_COLORS[m.overall_health] || '#6b7280'}20`,
                        color: HEALTH_COLORS[m.overall_health] || '#6b7280',
                      }}>{m.overall_health}</span>
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{m.prediction_count_24h.toLocaleString()}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                      {m.accuracy_current != null ? `${(m.accuracy_current * 100).toFixed(1)}%` : '—'}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', color: m.accuracy_delta != null && m.accuracy_delta < 0 ? '#ef4444' : '#22c55e' }}>
                      {m.accuracy_delta != null ? `${m.accuracy_delta > 0 ? '+' : ''}${(m.accuracy_delta * 100).toFixed(1)}%` : '—'}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                      {(m.input_drift_detected || m.concept_drift_detected) ? (
                        <span style={{ color: '#f59e0b', fontWeight: 600, fontSize: 11 }}>
                          {m.input_drift_detected && 'Input'}{m.input_drift_detected && m.concept_drift_detected && ' + '}{m.concept_drift_detected && 'Concept'}
                        </span>
                      ) : (
                        <span style={{ color: 'rgba(255,255,255,0.3)' }}>None</span>
                      )}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                      {m.data_quality_score != null ? `${(m.data_quality_score * 100).toFixed(0)}%` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Drift Events */}
      <div className="glass" style={{ padding: 20 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: 'rgba(255,255,255,0.8)' }}>
          Unresolved Drift Events
        </h3>
        {driftEvents.length === 0 ? (
          <p style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: 20 }}>No unresolved drift events</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {driftEvents.map(evt => (
              <div key={evt.id} className="glass" style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{
                  padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                  background: `${SEVERITY_COLORS[evt.severity] || '#6b7280'}20`,
                  color: SEVERITY_COLORS[evt.severity] || '#6b7280',
                }}>{evt.severity}</span>
                <span style={{ fontSize: 12, fontFamily: 'monospace', color: 'rgba(255,255,255,0.6)' }}>{evt.model_id.slice(0, 8)}</span>
                <span style={{ fontSize: 12, flex: 1 }}>{evt.drift_type}</span>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{new Date(evt.detected_at).toLocaleDateString()}</span>
                <button
                  onClick={() => handleResolve(evt.id)}
                  disabled={resolvingId === evt.id}
                  style={{
                    padding: '4px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
                    background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', color: '#22c55e',
                    opacity: resolvingId === evt.id ? 0.5 : 1,
                  }}
                >
                  {resolvingId === evt.id ? 'Resolving...' : 'Resolve'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ModelHealthPanel;
